"use client";

import { useEffect } from "react";

/**
 * Puts an unread counter in the tab title — "(2) Cybersecurity Services…" — when a
 * Fchat agent or bot replies while the visitor is looking at another tab.
 *
 * The widget renders inside the `<chat-widget>` shadow root, so its messages are
 * invisible to `document.querySelectorAll`; the observer has to be attached to that
 * shadow root instead (same constraint as FchatTheme.tsx, which skins the widget).
 */

/* Incoming = from the agent/bot. Our own messages use .chat_msg_item_user. */
const INCOMING = ".chat_msg_item_admin";

const ChatTitleNotifier = () => {
  useEffect(() => {
    let baseTitle = document.title;
    let unread = 0;

    const decorated = () => (unread > 0 ? `(${unread}) ${baseTitle}` : baseTitle);

    const render = () => {
      if (document.title !== decorated()) document.title = decorated();
    };

    const onVisibilityChange = () => {
      if (document.hidden) return;
      unread = 0;
      render();
    };

    // Next.js swaps the <title> on client-side navigation, so keep our base in sync
    // instead of badging a title from the page the visitor already left.
    const headObserver = new MutationObserver(() => {
      if (document.title === decorated()) return;
      baseTitle = document.title;
      render();
    });

    const countsAsIncoming = (node: Node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      const el = node as Element;
      return el.matches?.(INCOMING) || !!el.querySelector?.(INCOMING);
    };

    let widgetObserver: MutationObserver | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let giveUp: ReturnType<typeof setTimeout> | null = null;

    const stopPolling = () => {
      if (poll) {
        clearInterval(poll);
        poll = null;
      }
      if (giveUp) {
        clearTimeout(giveUp);
        giveUp = null;
      }
    };

    const attach = () => {
      const root = document.querySelector("chat-widget")?.shadowRoot;
      if (!root) return false;

      widgetObserver = new MutationObserver((mutations) => {
        if (!document.hidden) return;

        let added = 0;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (countsAsIncoming(node)) added += 1;
          });
        });

        if (added) {
          unread += added;
          render();
        }
      });
      widgetObserver.observe(root, { childList: true, subtree: true });
      return true;
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    headObserver.observe(document.head, { childList: true, subtree: true, characterData: true });

    // The widget script is lazyOnload, so it is not in the DOM on mount.
    if (!attach()) {
      poll = setInterval(() => {
        if (attach()) stopPolling();
      }, 500);
      giveUp = setTimeout(stopPolling, 60000);
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      headObserver.disconnect();
      widgetObserver?.disconnect();
      stopPolling();
      unread = 0;
      render();
    };
  }, []);

  return null; // This component doesn't render anything
};

export default ChatTitleNotifier;
